import React, { useEffect, useState, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "../../components/common/Loader";
import { ArrowLeft, Search } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/Opportunity/OpportunityViewStyles";
import * as XLSX from "xlsx-js-style";
import { DownArrow, UpArrow } from "../../components/svg/Icons";
import RowsPerPageSelector from "../../components/common/RowsPerPageSelector";
import Pagination from "../../components/common/Pagination";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

interface Quotation {
  id: string | number;
  quotation_number: string;
  quotation_type: string;
  quotation_status: string;
  quotation_subject: string;
  opportunity: string;
  valid_until: string;
  group_total: string;
  total: string;
  log: string;
  from_company_name?: string;
  from_address?: string;
  from_phone?: string;
  from_email?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
  terms_and_conditions?: string;
}

interface OpportunityData {
  id?: number;
  opportunitie_name: string;
  opp_amount: string;
  quotation_subject: string;
  expected_close_date: string;
  probability: string | number;
  status: string;
  stage: string;
  source: string;
  // assigned_to: string;
  account_name: string;
  log: string | null;
  description: string | null;
  quotation: "YES" | "NO";
  quotation_number?: string;
  quotation_type?: string;
  quotation_status?: string;
  valid_until?: string;
  total?: string;
  group_total?: string;
  from_company_name?: string;
  from_email?: string;
  from_phone?: string;
  from_address?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
  terms_and_conditions?: string | null;
  products?: Array<{
    product_id: number;
    product_name: string;
    price: string;
    quantity: number;
    IGST?: string;
    CGST?: string;
    SGST?: string;
    discount?: string;
    product_total?: string;
    description?: string;
  }>;
  services?: Array<{
    service_name: string;
    price: string;
    service_GST?: string;
    discount?: string;
    service_total?: string;
    description?: string | null;
  }>;
}

const OpportunityView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { opportunitie_id } = location.state || {};
  const [opportunity, setOpportunity] = useState<OpportunityData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<keyof Quotation | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("No access token found.");
      return null;
    }
    return token;
  };

  const axiosConfig = (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const fetchOpportunity = useCallback(async () => {
    if (!opportunitie_id) {
      setError("Opportunity ID not found.");
      setLoading(false);
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        setError("Unauthorized");
        setLoading(false);
        return;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/opportunities-quotation`,
        { opportunitie_id },
        axiosConfig(token)
      );

      if (response.status === 200 && response.data.status === 200) {
        const responseData = response.data.data;

        const dataArray = Array.isArray(responseData)
          ? responseData
          : [responseData];

        const firstOpportunity = dataArray[0] || null;
        setOpportunity(firstOpportunity);

        const quotationData = dataArray
          .filter(
            (data: any) => data.quotation === "YES" && data.quotation_number
          )
          .map((data: any, index: number) => ({
            id: data.id || `temp-${index}`,
            quotation_number: data.quotation_number || "",
            quotation_type: data.quotation_type || "N/A",
            quotation_status: data.quotation_status || "N/A",
            quotation_subject: data.quotation_subject || "",
            opportunity: data.opportunitie_name || "N/A",
            valid_until: data.valid_until || "N/A",
            group_total: data.group_total || "0.00",
            total: data.total || "0.00",
            log: data.log || "",
            from_company_name: data.from_company_name || "",
            from_email: data.from_email || "",
            from_phone: data.from_phone || "",
            from_address: data.from_address || "",
            to_client_name: data.to_client_name || "",
            to_address: data.to_address || "",
            to_email: data.to_email || "",
            terms_and_conditions: data.terms_and_conditions ?? undefined,
          }));

        setQuotations(quotationData);

        if (quotationData.length === 0) {
          console.log("No valid quotations found.");
        }
      } else {
        setError(
          response.data.message || "Failed to fetch opportunity details."
        );
        setQuotations([]);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message ||
          "An error occurred while fetching opportunity data."
          : "An unexpected error occurred."
      );
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  }, [opportunitie_id]);

  const handleSort = (column: keyof Quotation) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredQuotations = quotations.filter((quotation) =>
    Object.values(quotation)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedQuotations = [...filteredQuotations].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];

    if (valA === undefined || valA === null) return 1;
    if (valB === undefined || valB === null) return -1;

    if (sortColumn === "group_total" || sortColumn === "total") {
      const numA = parseFloat(valA as string);
      const numB = parseFloat(valB as string);
      if (isNaN(numA) || isNaN(numB)) return 0;
      return sortOrder === "asc" ? numA - numB : numB - numA;
    }

    if (sortColumn === "valid_until") {
      const dateA = new Date(valA as string);
      const dateB = new Date(valB as string);
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
      return sortOrder === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    const strA = (valA as string).toLowerCase();
    const strB = (valB as string).toLowerCase();
    return sortOrder === "asc"
      ? strA.localeCompare(strB)
      : strB.localeCompare(strA);
  });

  const isTabletView = window.innerWidth < 768;
  const displayedQuotations = isTabletView
    ? sortedQuotations
    : sortedQuotations.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  const totalPages = isTabletView
    ? 1
    : Math.ceil(filteredQuotations.length / rowsPerPage);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredQuotations
      : sortColumn
        ? sortedQuotations
        : quotations;

    if (!dataToDownload.length) {
      alert("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((quotation) => ({
      "Quotation Number": quotation.quotation_number,
      "Quotation Type": quotation.quotation_type,
      "Quotation Status": quotation.quotation_status,
      Opportunity: quotation.opportunity,
      "Valid Till": quotation.valid_until,
      "Group Total": quotation.group_total,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    XLSX.utils.sheet_add_aoa(worksheet, [["Quotation List"]], { origin: "A1" });

    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i);
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "Quotation List".length
        ) + 2,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");
    XLSX.writeFile(
      workbook,
      searchTerm ? `QuotationData_${searchTerm}.xlsx` : "QuotationData.xlsx"
    );
  };

  const handleViewClick = (quotationNumber: string) => {
    navigate("/opportunity-quotation-view", {
      state: { quotationNumber, opportunitie_id },
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, isTabletView]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  if (loading) return <Loader />;
  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!opportunity)
    return (
      <p className="text-center text-gray-500">
        No opportunity data available.
      </p>
    );

  return (
    <div
      className="max-w-7xl mx-auto text-gray-800 dark:text-gray-100"
      style={{
        fontFamily: "Lato, sans-serif",
        fontSize: "12px",
        fontWeight: "normal",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex items-center px-0 py-1 justify-between">
          <h3 className="text-theme-xl font-semibold text-gray-800 dark:text-white/90 text-center">
            Opportunity Details
          </h3>
          <Link
            to="/opportunity-list"
            className="px-3 py-1.5 bg-[#2f6aa7] dark:bg-[#3d6794] hover:bg-[#2779bd] dark:hover:bg-[#335877] text-sm text-white dark:text-gray-300 rounded-lg flex items-center"
          >
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>
        <div className="mt-6">
          <div className="mt-6 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 shadow-sm p-4 rounded-xl transition-colors duration-300">
            <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Opportunity Information
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Opportunity Name
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.opportunitie_name || "N/A"}
                </p>
              </div>
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Account Name
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.account_name || "N/A"}
                </p>
              </div>
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Amount
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  ₹
                  {opportunity.opp_amount &&
                    !isNaN(parseFloat(opportunity.opp_amount))
                    ? parseFloat(opportunity.opp_amount).toLocaleString(
                      "en-IN",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )
                    : "0.00"}
                </p>
              </div>
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Expected Close Date
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.expected_close_date || "N/A"}
                </p>
              </div>
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Probability
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.probability
                    ? `${opportunity.probability}%`
                    : "N/A"}
                </p>
              </div>
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Source
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.source || "N/A"}
                </p>
              </div>
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Stage
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.stage || "N/A"}
                </p>
              </div>              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Status
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.status || "N/A"}
                </p>
              </div>

              {/* <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Assigned To
                </h6>
                <p className="text-gray-800 dark:text-gray-100">
                  {opportunity.assigned_to || "N/A"}
                </p>
              </div> */}
              <div>
                <h6 className="text-sm text-gray-500 dark:text-gray-400">
                  Log
                </h6>
                {opportunity.log ? (
                  opportunity.log.split(" | ").map((line, index) => (
                    <p key={index} className="text-gray-800 dark:text-gray-100">
                      {line}
                    </p>
                  ))
                ) : (
                  <p className="text-gray-800 dark:text-gray-100">
                    No logs available
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 shadow-sm p-4 rounded-xl transition-colors duration-300">
            <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Quotation Details for {opportunity.account_name || "N/A"}
            </h5>
            {quotations.length > 0 ? (
              <div className={styles.tableCard}>
                <div className={styles.headerControls}>
                  <RowsPerPageSelector
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                  />
                  <div className={styles.controlsInner}>
                    <div className={styles.searchContainer}>
                      <Search className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Search..."
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={handleDownload}
                    >
                      Download
                      <DownloadIcon className={styles.downloadIcon} />
                    </button>
                  </div>
                </div>
                {error && <p className={styles.errorText}>{error}</p>}

                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr className={styles.theadRow}>
                        <th
                          className={styles.thName}
                          onClick={() => handleSort("quotation_number")}
                        >
                          <div className={styles.thSortable}>
                            <p className={styles.thText}>Number</p>
                            <button className={styles.sortButton}>
                              <DownArrow />
                              <UpArrow />
                            </button>
                          </div>
                        </th>
                        <th
                          className={styles.thTexts}
                          onClick={() => handleSort("quotation_type")}
                        >
                          <div className={styles.thSortable}>
                            <p className={styles.thText}>Type</p>
                            <button className={styles.sortButton}>
                              <DownArrow />
                              <UpArrow />
                            </button>
                          </div>
                        </th>
                        <th
                          className={styles.thTexts}
                          onClick={() => handleSort("quotation_status")}
                        >
                          <div className={styles.thSortable}>
                            <p className={styles.thText}>Status</p>
                            <button className={styles.sortButton}>
                              <DownArrow />
                              <UpArrow />
                            </button>
                          </div>
                        </th>
                        <th
                          className={styles.thTexts}
                          onClick={() => handleSort("opportunity")}
                        >
                          <div className={styles.thSortable}>
                            <p className={styles.thText}>Opportunity</p>
                            <button className={styles.sortButton}>
                              <DownArrow />
                              <UpArrow />
                            </button>
                          </div>
                        </th>
                        <th
                          className={styles.thTexts}
                          onClick={() => handleSort("valid_until")}
                        >
                          <div className={styles.thSortable}>
                            <p className={styles.thText}>Valid Till</p>
                            <button className={styles.sortButton}>
                              <DownArrow />
                              <UpArrow />
                            </button>
                          </div>
                        </th>
                        <th
                          className={styles.thName}
                          onClick={() => handleSort("group_total")}
                        >
                          <div className={styles.thSortable}>
                            <p className={styles.thText}>Group Total</p>
                            <button className={styles.sortButton}>
                              <DownArrow />
                              <UpArrow />
                            </button>
                          </div>
                        </th>
                        <th className={styles.thAction}>
                          <p className={styles.thText}>Action</p>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            <Loader />
                          </td>
                        </tr>
                      ) : displayedQuotations.length > 0 ? (
                        displayedQuotations.map((quotation, index) => (
                          <tr key={index} className={styles.tbodyRow}>
                            <td className={styles.tdName}>
                              {quotation.quotation_number}
                            </td>
                            <td className={styles.tdName}>
                              {quotation.quotation_type}
                            </td>
                            <td className={styles.tdName}>
                              {quotation.quotation_status}
                            </td>
                            <td className={styles.tdName}>
                              {quotation.opportunity}
                            </td>
                            <td className={styles.tdName}>
                              {quotation.valid_until}
                            </td>
                            <td className={styles.tdName}>
                              ₹
                              {parseFloat(quotation.group_total).toLocaleString(
                                "en-IN",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>
                            <td className={styles.tdAction}>
                              <div className={styles.actionContainer}>
                                <Tooltip
                                  content="Quotation View"
                                  bgColor="bg-green-600"
                                >
                                  <button
                                    onClick={() =>
                                      handleViewClick(
                                        quotation.quotation_number
                                      )
                                    }
                                    className={styles.viewButton}
                                  >
                                    <FontAwesomeIcon
                                      icon={faEye}
                                      className="text-white text-sm"
                                    />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className={styles.noDataText}>
                            This opportunity has no quotation.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {!isTabletView && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    filteredLength={filteredQuotations.length}
                    handlePageChange={handlePageChange}
                  />
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                This opportunity has no quotation.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityView;
